import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, SectionList, SafeAreaView, Linking, TouchableOpacity } from 'react-native'
import { format } from 'date-fns'
import { Icon, Text, Card, RangeDatepicker, Spinner, Button, Toggle, Datepicker, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import { fieldValue, getProductMovementOnDateRange, updateProduct, updateStockManually, updateMovement, deleteMovement } from '../../global/Firebase/firebase'
import Divider from '../../components/Divider'
import StyledTextInput from '../../components/StyledTextInput'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { useTranslation } from 'react-i18next'
import { localeDateServiceAtom, productsDictAtom, storesDictAtom, userRoleAtom } from '../../global/AtomsProvider'
import { dateRangeWithTimezoneAndAutoclose, getLocalStoreDate, getDailyStatID, normalizeDate } from '../../utils/timezoneFunctions'
import { roundNumber } from '../../utils/numberUtils'
import { useAtomValue } from 'jotai'
import { ModalView } from '../../components/ModalView'
import { validateMovementsWithIncrements, validateProductStocks } from '../../utils/validationFunctions'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { stockThresholdInputValue } from '../../utils/productFormFields'

const incorrectPreviosInStock = ['deleteSupply', 'deleteManufacture', 'deleteSale', 'deleteWriteOff', 'deleteComplexSale', 'editComplexSale']

export default function ProductStockMovementScreen( { navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const { product, store: passedStore } = route.params
  const productsDict = useAtomValue(productsDictAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const userRole = useAtomValue(userRoleAtom)
  const [range, setRange] = useState({})
  const [data, setData] = useState(null)
  const [stats, setStats] = useState({
    saleCount: 0,
    supplyCount: 0,
    manufactureCount: 0,
    revisionCount: 0,
    returnCount: 0,
    writeOffCount: 0,
    deleteSupplyCount: 0,
    deleteManufactureCount: 0,
    deleteWriteOffCount: 0,
    deleteSaleCount: 0,
    deleteReturnCount: 0,
    deleteRevisionCount: 0,
  })
  const [store, setStore] = useState(passedStore ?? context.user.store?.id)
  const [importance, setImportance] = useState(product.importance)
  const [stockThreshold, setStockThreshold] = useState(stockThresholdInputValue(product.stockThreshold))
  const [isLoading, setIsLoading] = useState(true)
  const [saveError, setSaveError] = useState('')
  const [isSaveLoading, setIsSaveLoading] = useState(false)
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(true)
  const [validationDate, setValidationDate] = useState(null)
  const [isValidationLoading, setIsValidationLoading] = useState(false)
  const [foundErrorModalIsShown, setFoundErrorModalIsShown] = useState(false)
  const [newStockValue, setNewStockValue] = useState('')
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [selectedMovement, setSelectedMovement] = useState(null)
  const [editedMovement, setEditedMovement] = useState(null)
  const [isEditLoading, setIsEditLoading] = useState(false)
  const [editError, setEditError] = useState('')
  const _rangePicker = React.createRef()
  const _datepicker = React.createRef()

  useEffect(() => {
    var today = new Date()
    var d = new Date()
    d.setDate(today.getDate() - 3)
    setRange({startDate: d, endDate: today})
    getDataFromFB({startDate: d, endDate: today})
  }, [store])
  
  useEffect(() => {
    navigation.setOptions({title: product.name})
  }, [])

  const getDataFromFB = async (range, validate=false) => {
    if (range.startDate != null && range.endDate != null) {
      _rangePicker.current?.blur()
      setIsLoading(true)
      setData([])
      let localDateRange = dateRangeWithTimezoneAndAutoclose(range, [store])
      let docs = await getProductMovementOnDateRange(db, product.id, localDateRange, false, store)
      var newProduct = 0
      var sale = 0
      var writeOff = 0
      var revision = 0
      var returns = 0
      var supply = 0
      var manufacture = 0
      var transfer = 0
      var deleteSale = 0
      var deleteWriteOff = 0
      var deleteSupply = 0
      var deleteManufacture = 0
      var deleteReturn = 0
      var deleteRevision = 0
      var complexSale = 0
      var deleteComplexSale = 0
      var editComplexSale = 0
      var manualChange = 0
      var totalMovement = 0
      for (const [index1, dateData] of docs.entries()) {
        for (const [index2, value] of dateData.data.entries()) {
          if (value.type === 'newProduct') {
            newProduct += (value.increment || 0)
          }
          if (value.type === 'sale') {
            sale += value.increment || 0
          }
          if (value.type === 'writeOff') {
            writeOff += value.increment || 0
          }
          if (value.type === 'revision') {
            revision += value.increment || 0
          }
          if (value.type === 'return') {
            returns += value.increment || 0
          }
          if (value.type === 'supply') {
            supply += value.increment || 0
          }
          if (value.type === 'manufacture') {
            manufacture += value.increment || 0
          }
          if (value.type === 'transfer') {
            transfer += value.increment || 0
          }
          if (value.type === 'deleteSupply') {
            deleteSupply += value.increment || 0
          }
          if (value.type === 'deleteManufacture') {
            deleteManufacture += value.increment || 0
          }
          if (value.type === 'deleteSale') {
            deleteSale += value.increment || 0
          }
          if (value.type === 'deleteReturn') {
            deleteReturn += value.increment || 0
          }
          if (value.type === 'deleteWriteOff') {
            deleteWriteOff += value.increment || 0
          }
          if (value.type === 'deleteRevision') {
            deleteRevision += value.increment || 0
          }
          if (value.type === 'complexSale') {
            complexSale += value.increment || 0
          }
          if (value.type === 'deleteComplexSale') {
            deleteComplexSale += value.increment || 0
          }
          if (value.type === 'editComplexSale') {
            editComplexSale += value.increment || 0
          }
          if (value.type === 'manualChange') {
            manualChange += value.increment || 0
          }
          if (value.type !== 'revision' && value.type !== 'deleteRevision' && value.type !== 'manualChange') {
            totalMovement += (value.increment || 0)
          }
        }
      }

      docs = validateMovementsWithIncrements(docs)

      // get product stock snapshots 
      let startingStatId = getDailyStatID(store, localDateRange.startDate)
      let startingSnap = await db.collection('productStockSnapshots').doc(startingStatId + '-stock').get()
      let startingStockSnapshot = startingSnap.data()
      // console.log(startingSnap.id, 'starting stock snapshot', startingStockSnapshot)
      let endingStatId = getDailyStatID(store, localDateRange.endDate)
      let endingSnap = await db.collection('productStockSnapshots').doc(endingStatId + '-stock').get()
      let endingStockSnapshot = endingSnap.data()
      // console.log(endingSnap.id, 'ending stock snapshot', endingStockSnapshot)

      let newStats = {
        newProductCount: newProduct,
        saleCount: sale,
        writeOffCount: writeOff,
        revisionCount: revision,
        returnCount: returns,
        supplyCount: supply,
        manufactureCount: manufacture,
        transferCount: transfer,
        deleteSupplyCount: deleteSupply,
        deleteManufactureCount: deleteManufacture,
        deleteWriteOffCount: deleteWriteOff,
        deleteSaleCount: deleteSale,
        deleteReturnCount: deleteReturn,
        deleteComplexSaleCount: deleteComplexSale,
        deleteRevisionCount: deleteRevision,
        editComplexSaleCount: editComplexSale,
        complexSaleCount: complexSale,
        manualChangeCount: manualChange,
        endingStockSnapshot: endingStockSnapshot?.[product.id], 
        startingStockSnapshot: startingStockSnapshot?.[product.id],
        totalMovement,
        startingStock: docs[docs.length-1]?.data[docs[docs.length-1]?.data.length-1].previousInStock || 0,
      }
      if (newStats.startingStockSnapshot && newStats.startingStockSnapshot !== newStats.startingStock) { 
        console.log('starting stock snapshot is not equal to movements starting stock', newStats.startingStockSnapshot, newStats.startingStock)
      }
      setStats(newStats)
      setData(docs)

      // Log movement data for E2E testing
      const allMovements = docs.flatMap(d => d.data || [])
      console.log('[PRODUCT_MOVEMENT_DATA]', JSON.stringify(sanitizeForFirestore({
        productId: product.id,
        productName: product.name,
        store,
        stats: newStats,
        movementCount: allMovements.length,
        firstMovements: allMovements.slice(0, 5).map(m => ({
          type: m.type,
          increment: m.increment,
          previousInStock: m.previousInStock,
        })),
      })))

      setIsLoading(false)
      // if (validate) {
      //   product.ref.update({
      // }
    }
  }
  
  const openEditModal = (movement) => {
    setSelectedMovement(movement)
    setEditedMovement({
      type: movement.type || '',
      increment: movement.increment?.toString() || '0',
      previousInStock: movement.previousInStock?.toString() || '0',
    })
    setEditError('')
    setEditModalVisible(true)
  }

  const handleSaveMovement = async () => {
    if (!selectedMovement || !editedMovement) return
    
    setIsEditLoading(true)
    setEditError('')
    
    try {
      const updatedFields = {
        type: editedMovement.type,
        increment: parseFloat(editedMovement.increment) || 0,
        previousInStock: parseFloat(editedMovement.previousInStock) || 0,
      }
      
      await updateMovement(selectedMovement, updatedFields)
      setEditModalVisible(false)
      // Reload data
      getDataFromFB(range)
    } catch (error) {
      console.error('Error updating movement:', error)
      setEditError(error.message || 'Error updating movement')
    } finally {
      setIsEditLoading(false)
    }
  }

  const handleDeleteMovement = async () => {
    if (!selectedMovement) return
    
    setIsEditLoading(true)
    setEditError('')
    
    try {
      await deleteMovement(selectedMovement)
      setEditModalVisible(false)
      // Reload data
      getDataFromFB(range)
    } catch (error) {
      console.error('Error deleting movement:', error)
      setEditError(error.message || 'Error deleting movement')
    } finally {
      setIsEditLoading(false)
    }
  }

  const renderItem = ({item, index}) => {
    const date = item.date ? format(getLocalStoreDate(store, normalizeDate(item.date)), "HH:mm") : t('now')
    return (
        <Card
          onPress={() => {
            if (context.user.email === 'test2@test2.com') openEditModal(item)
          }}
          style={styles.item}
        > 
        <View style={styles.dateTimeView}>
          <Icon name='clock-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {date}
          </Text>
        </View>
        <View style={styles.dateTimeView}>
          {/* <Icon name='clock-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} /> */}
          <Text
            style={styles.itemTitle}
            category='s2'>
            {t('productStocks.type') + ': ' + t('productStocks.'+item.type)}
          </Text>
        </View>
        <View style={styles.dateTimeView}>
          {/* <Icon name='clock-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} /> */}
          <Text
            style={styles.itemTitle}
            category='s2'>
            {t('productStocks.increment') + ': ' + roundNumber(item.increment)}
          </Text>
        </View>
        { showDetails && (
          <>
            <View style={styles.dateTimeView}>
              <Text
                style={styles.itemTitle}
                category='s2'>
                {t('productStocks.previousInStock') + ': ' + roundNumber(item.previousInStock)}
              </Text>
            </View>
            { item.pastDate && (
              <View style={styles.dateTimeView}>
                <Text
                  style={styles.itemTitle}
                  category='s2' status='danger'>
                  {t('productStocks.savedPastDate') + ': ' + format(getLocalStoreDate(store, normalizeDate(item.linkedDocDate)), "d/MM/y HH:mm")}
                </Text>
              </View>
            )}
            { item.savedToServerLater && (
              <View style={styles.dateTimeView}>
                <Text
                  style={styles.itemTitle}
                  category='s2' status='danger'>
                  {t('productStocks.savedToServerLater') + ': ' + format(getLocalStoreDate(store, normalizeDate(item.clientSaveDate)), "d/MM/y HH:mm")}
                </Text>
              </View>
            )}
            { item.isCorrect === false && (
              <View style={styles.dateTimeView}>
                <Text
                  style={styles.itemTitle}
                  category='s2' status='danger'>
                  {t('productStocks.possibleStockError')}
                </Text>
              </View>
            )}
          </>
        ) }
      </Card>
    )
  }

  const statHeader = () => (
    <>
      {context.user.role === 'admin' && 
        <Button style={{alignSelf: 'flex-end', marginRight: -20}} appearance='ghost' size='small' onPress={() => {
          Linking.openURL('https://atlas-app.notion.site/3f7b07908b974a92918338fc0ddf65fc')
        }} accessoryLeft={(props) => (<Icon name='question-mark-circle-outline' {...props} style={{...props.style, marginRight: 0}} />)}>{t('productStocks.instructions')}</Button>
      }
      <Text category='h3' style={styles.title}>
        {product.name}
      </Text>
      <Text category='h6' style={styles.title}>
        {t('productStocks.changeStock')}
      </Text>
      {(userRole?.permissions?.['Supplies'] || context.user.role === 'admin') &&
        <Button style={styles.submitButton} status='basic' onPress={async () => {
          navigation.navigate('SupplyDetail', {preselectedItems: [product], preselectedStore: store})
        }}>{t('productStocks.createSupply')}</Button>
      }
      {(userRole?.permissions?.['WriteOffs'] || context.user.role === 'admin') &&
        <Button style={styles.submitButton} status='basic' onPress={async () => {
          navigation.navigate('WriteOffDetail', {preselectedProduct: product, preselectedStore: store})
        }}>{t('productStocks.createWriteOff')}</Button>
      }
      {(userRole?.permissions?.['Revisions'] || context.user.role === 'admin') &&
        <Button style={styles.submitButton} status='basic' onPress={async () => {
          navigation.navigate('RevisionDetail', {preselectedProduct: product, preselectedStore: store})
        }}>{t('productStocks.createRevision')}</Button>
      }
      {(userRole?.permissions?.['Products'] || context.user.role === 'admin') &&
      <>
        <Text category='h6' style={styles.title}>
          {t('productStocks.productInfo')}
        </Text>
        <View style={{padding: 15, backgroundColor: theme['background-basic-color-1'], borderRadius: 10}}>
          <Toggle 
            checked={importance > 1}
            onChange={(checked) => {
              setImportance(checked ? 2 : 1)
            }}
            style={{alignSelf: 'flex-start', }}
          >
            {t('productStocks.showInImportantSection')}
          </Toggle>
          <Text category='label' style={styles.label}>
            {t('productStocks.stockThreshold')}
          </Text>
          <StyledTextInput
            value={stockThreshold}
            onChangeText={setStockThreshold}
            keyboardType='decimal-pad'
            style={{marginBottom: 10}}
            returnKeyType='done'
            placeholder='4'
          />
          {successMessageVisible && (
            <>
              <Text status='success' style={styles.messageText}>{t('success')}</Text>
              <Button appearance='ghost' onPress={() => navigation.goBack()} style={styles.submitButton}>
                {t('back')}
              </Button>
            </>
          )} 
          {saveError != '' && (
            <Text style={styles.messageText} status='danger'>{saveError}</Text>
          )} 
          {isSaveLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            !successMessageVisible && (
              <Button appearance='ghost' onPress={async () => {
                  setIsSaveLoading(true)
                  let newValues = {
                    stockThreshold: parseFloat(stockThreshold),
                    importance: parseInt(importance),
                    dailyStatID: getDailyStatID(store, new Date()),
                    lastChangeStore: store,
                    lastChangeAt: new Date(), 
                    organizationId: context.user.organizationId
                  }
                  let lastChangeType = 'updateProductStockMovement'
                  await updateProduct(db, product, {...newValues, lastChangeType}, productsDict)
                  setIsSaveLoading(false)
                  setSuccessMessageVisible(true)
              }}>{t('save')}</Button>
            )
          )}
        </View>
        </>
      }
      <Text category='h6' style={{marginTop: 15, marginBottom: 5}}>
        {t('productStocks.productStockMovementHistory')}
      </Text>
      { Object.keys(storesDict).length > 1 && (
        <View style={styles.storeChooser}>
          { Object.entries(storesDict).map(([key, value]) => (
            <Button style={styles.storeButton} key={key} appearance='outline' status={store === key ? 'primary' : 'basic'} onPress={() => {setStore(key)}}>{value.name}</Button>
          ))}
        </View>
      )}
      <View style={styles.dateFilter}>
        <Text category='label' style={styles.label}>
          {t('filterByDate')}
        </Text>
        <RangeDatepicker
          placement='top'
          min={new Date('2020-12-01')}
          max={new Date('2029-12-31')}
          range={range}
          ref={_rangePicker}
          dateService={localeDateService}
          onSelect={(newRange) => {
            if (newRange.endDate) {
              setRange(newRange)
              getDataFromFB(newRange)
            } else if (newRange.startDate && range.startDate === newRange.startDate) {
              let endDate = new Date(newRange.startDate)
              endDate.setHours('23', '59', '59', '99')
              setRange({startDate: newRange.startDate, endDate})
              getDataFromFB({startDate: newRange.startDate, endDate})
            } else {
              setRange(newRange)
            }
          }}
        />
      </View>
      { data?.length > 0 ? (
        <View style={styles.statContainer}>
          <View style={styles.row}>
            <Text style={styles.statLabel}>{t('productStocks.startingStock')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.startingStock)}</Text>
          </View>
          <Divider />
          { stats.newProductCount > 0 && (
            <View style={styles.row}>
              <Text style={styles.statLabel}>{t('productStocks.newProductCount')}</Text>
              <Text style={styles.statValue}>{roundNumber(stats.newProductCount)}</Text>
            </View>
          )}
          <View style={styles.row}>
            <Text style={styles.statLabel}>{t('productStocks.saleCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.saleCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}> - {t('productStocks.deleteSaleCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.deleteSaleCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}>{t('productStocks.complexSaleCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.complexSaleCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}> - {t('productStocks.editComplexSaleCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.editComplexSaleCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}> - {t('productStocks.deleteComplexSaleCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.deleteComplexSaleCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}>{t('productStocks.writeOffCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.writeOffCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}> - {t('productStocks.deleteWriteOffCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.deleteWriteOffCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}>{t('productStocks.supplyCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.supplyCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}> - {t('productStocks.deleteSupplyCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.deleteSupplyCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}>{t('productStocks.manufactureCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.manufactureCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}> - {t('productStocks.deleteManufactureCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.deleteManufactureCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}>{t('productStocks.transferCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.transferCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}>{t('productStocks.returnCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.returnCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}>- {t('productStocks.deleteReturnCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.deleteReturnCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text category='s1' style={styles.statLabel}>{t('productStocks.totalMovement')}</Text>
            <Text category='s1' style={styles.statValue}>{roundNumber(stats.totalMovement)}</Text>
          </View>
          <Divider />
          <View style={styles.row}>
            <Text category='s1' style={styles.statLabel}>{t('productStocks.endingStockShouldBe')}</Text>
            <Text category='s1' style={styles.statValue}>{roundNumber(stats.startingStock + stats.totalMovement)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}>{t('productStocks.revisionCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.revisionCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}> - {t('productStocks.deleteRevisionCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.deleteRevisionCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}>{t('productStocks.manualChangeCount')}</Text>
            <Text style={styles.statValue}>{roundNumber(stats.manualChangeCount)}</Text>
          </View>
          <View style={styles.row}>
            <Text category='s1' style={styles.statLabel}>{t('productStocks.endingStockWithRevisions')}</Text>
            <Text category='s1' style={styles.statValue}>{roundNumber(
                stats.startingStock + stats.totalMovement + stats.revisionCount + stats.deleteRevisionCount + stats.manualChangeCount
            )}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.statLabel}>{t('productStocks.currentStock')}</Text>
            <Text style={styles.statValue}>{roundNumber(product.storeStock[store])}</Text>
          </View>
          <Divider />
        </View>
      ) : (
        <Text>{t('productStocks.noMovement')}</Text>
      )}
    </>
  )

  const movementTypes = [
    { text: t('productStocks.sale'), value: 'sale' },
    { text: t('productStocks.supply'), value: 'supply' },
    { text: t('productStocks.writeOff'), value: 'writeOff' },
    { text: t('productStocks.revision'), value: 'revision' },
    { text: t('productStocks.return'), value: 'return' },
    { text: t('productStocks.manufacture'), value: 'manufacture' },
    { text: t('productStocks.transfer'), value: 'transfer' },
    { text: t('productStocks.complexSale'), value: 'complexSale' },
    { text: t('productStocks.manualChange'), value: 'manualChange' },
    { text: t('productStocks.newProduct'), value: 'newProduct' },
    { text: t('productStocks.deleteSale'), value: 'deleteSale' },
    { text: t('productStocks.deleteSupply'), value: 'deleteSupply' },
    { text: t('productStocks.deleteWriteOff'), value: 'deleteWriteOff' },
    { text: t('productStocks.deleteRevision'), value: 'deleteRevision' },
    { text: t('productStocks.deleteReturn'), value: 'deleteReturn' },
    { text: t('productStocks.deleteManufacture'), value: 'deleteManufacture' },
    { text: t('productStocks.deleteComplexSale'), value: 'deleteComplexSale' },
    { text: t('productStocks.editComplexSale'), value: 'editComplexSale' },
  ]

  const renderFooter = () => (
    <View>
      <ModalView isVisible={editModalVisible} setIsVisible={setEditModalVisible}>
        {editedMovement && (
          <>
            <Text style={{marginBottom: 5}} category="h6">{t('productStocks.editMovement')}</Text>
            <Text category='label' style={styles.label}>{t('productStocks.type')}</Text>
            <PlatformDependantSelect
              data={movementTypes}
              selectedOption={movementTypes.find(t => t.value === editedMovement.type) || movementTypes[0]}
              onSelect={(option) => setEditedMovement({...editedMovement, type: option.value})}
              style={{marginBottom: 10}}
            />
            <Text category='label' style={styles.label}>{t('productStocks.previousInStock')}</Text>
            <StyledTextInput 
              value={editedMovement.previousInStock}
              onChangeText={(text) => setEditedMovement({...editedMovement, previousInStock: text})}
              keyboardType='decimal-pad'
              style={{marginBottom: 10}}
              returnKeyType='done'
            />
            <Text category='label' style={styles.label}>{t('productStocks.increment')}</Text>
            <StyledTextInput 
              value={editedMovement.increment}
              onChangeText={(text) => setEditedMovement({...editedMovement, increment: text})}
              keyboardType='decimal-pad'
              style={{marginBottom: 10}}
              returnKeyType='done'
            />
            {editError && (
              <Text status='danger' style={styles.messageText}>{editError}</Text>
            )}
            {isEditLoading ? (
              <View style={{alignItems: 'center', marginTop: 10}}><Spinner /></View>
            ) : (
              <>
                <Button style={styles.submitButton} onPress={handleSaveMovement}>{t('save')}</Button>
                <Button status='danger' style={styles.submitButton} onPress={handleDeleteMovement}>{t('delete')}</Button>
                <Button appearance='ghost' onPress={() => setEditModalVisible(false)} style={styles.submitButton}>
                  {t('cancel')}
                </Button>
              </>
            )}
          </>
        )}
      </ModalView>
      <ModalView isVisible={foundErrorModalIsShown} setIsVisible={setFoundErrorModalIsShown}>
        <Text style={{marginBottom: 5}} category="h6">{t('productStocks.foundError')}</Text>
        <Text>{t('productStocks.foundErrorDescription')}</Text>
        <Text category='label' style={styles.label}>{t('productStocks.newStockValue')}</Text>
        <StyledTextInput 
          value={newStockValue}
          onChangeText={setNewStockValue}
          keyboardType='decimal-pad'
          style={{marginBottom: 10}}
          returnKeyType='done'
        />
        {successMessageVisible && (
          <>
            <Text status='success' style={styles.messageText}>{t('productStocks.successManualUpdate')}</Text>
            <Button appearance='ghost' onPress={() => navigation.goBack()} style={styles.submitButton}>
              {t('back')}
            </Button>
          </>
        )} 
        {saveError != '' && (
          <Text style={styles.messageText} status='danger'>{saveError}</Text>
        )} 
        { isValidationLoading 
          ? 
            <View style={{alignItems: 'center'}}><Spinner /></View>
          :
            !successMessageVisible && (<Button onPress={async () => {
              setIsValidationLoading(true)
              let numberAmount = parseFloat(newStockValue)
              if (isNaN(numberAmount)) {
                setSaveError(t('notANumberError'))
                return
              }
              try {
                await updateStockManually(product, store, numberAmount)
                setSuccessMessageVisible(true)
              } catch(error) {
                console.log(error)
              }
              setIsValidationLoading(false)
            }}>{t('save')}</Button>)
        }
      </ModalView>
      { context.user.role === 'admin' && <Button appearance="ghost" status='basic' onPress={() => setFoundErrorModalIsShown(true)}>{t('productStocks.foundError')}</Button> }
      <View style={{height: 100}}></View>
    </View>
  )

  return (
    isLoading
      ? (
        <View style={{alignItems: 'center', marginTop: 20}}><Spinner size="large" /></View>
      )
      : (
          <SectionList
            style={styles.list}
            sections={data}
            ListHeaderComponent={statHeader()}
            keyExtractor={(item, index) => item + index}
            renderItem={renderItem}
            renderSectionHeader={({ section: { title } }) => (
              <Text category='label' style={styles.label}>{title}</Text>
            )}
            ListFooterComponent={renderFooter()}
          />
      )
  )
}

const styles = StyleSheet.create({
  title: {
    marginTop: 10,
    marginBottom: 5,
  },
  statContainer: {
    marginBottom: 20,
  }, 
  list: {
    paddingHorizontal: 20,
    marginBottom: 25
  },
  icon: {
    marginRight: 4,
  },
  itemTitle: {
    marginRight: 25,
  },
  dateTimeView: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  label: {
    marginTop: 10,
    marginBottom: 5,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  storeChooser: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 5,
    marginTop: 10,
    justifyContent: 'center'
  }, 
  storeButton: {
    marginLeft: 5,
    marginBottom: 5,
  },
  submitButton: {
    marginBottom: 5
  },
  dateFilter: {
    marginBottom: 10
  }
})
